import { useCallback, useEffect, useState } from 'react';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import { useDashboard } from '../../../context/dashboardContext';
import ConfirmModal from '../../../components/shareable/confirmModal';
import ConveyorPagination from '../../../components/shareable/customerPagination';
import toast from 'react-hot-toast';
import { useSearch } from '../../../context/SearchContext';
import ConveyorTable from './components/conveyorTable';
import CreateConveyorModal from './components/createConveyorModal';
import UpdateConveyorModal from './components/updateConveyorModal';

const ModularConveyors = () => {
  const {
    getModularConveyors, 
    createModularConveyor, 
    updateModularConveyor, 
    deleteModularConveyor
  } = useDashboard();

  const { searchQuery, updateSearch } = useSearch();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [allConveyors, setAllConveyors] = useState([]);
  const [filteredConveyors, setFilteredConveyors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedConveyor, setSelectedConveyor] = useState(null);
  const itemsPerPage = 10;

  // Set the page in search context when component mounts
  useEffect(() => {
    updateSearch('', 'modularConveyors');
    
    // Clear search when component unmounts
    return () => updateSearch('', '');
  }, [updateSearch]);

  // Filter conveyors based on search query
  const filterConveyors = useCallback((conveyors, query) => {
    if (!query.trim()) return conveyors;
    
    const searchTerm = query.toLowerCase().trim();
    return conveyors.filter(conveyor => {
      return (
        (conveyor.item_number?.toString().toLowerCase() || '').includes(searchTerm) ||
        (conveyor.conveyor_name?.toString().toLowerCase() || '').includes(searchTerm) ||
        (conveyor.conveyor_style?.toString().toLowerCase() || '').includes(searchTerm) ||
        (conveyor.module_id?.toString().toLowerCase() || '').includes(searchTerm) ||
        (conveyor.part_number?.toString().toLowerCase() || '').includes(searchTerm) ||
        (conveyor.image_name?.toString().toLowerCase() || '').includes(searchTerm) 
      );
    });
  }, []);

  const fetchConveyors = useCallback(async (preservePage = false) => {
    setLoading(true);
    const result = await getModularConveyors();
    if (result.success) {
      // Sort conveyors by creation date (using _id timestamp) in descending order (latest first)
      const sortedConveyors = [...result.conveyors].sort((a, b) => {
        // Extract timestamp from MongoDB ObjectId
        const aTimestamp = parseInt(a._id.substring(0, 8), 16);
        const bTimestamp = parseInt(b._id.substring(0, 8), 16);
        return bTimestamp - aTimestamp; // Sort in descending order
      });
      
      setAllConveyors(sortedConveyors);
      
      // Apply search filter if there's a query
      const filtered = filterConveyors(sortedConveyors, searchQuery);
      setFilteredConveyors(filtered);
      
      // Preserve current page if requested (for updates)
      if (!preservePage) {
        // Only reset page on initial load or search change, not on updates
      }
    } else {
      toast.error('Failed to fetch conveyors');
    }
    setLoading(false);
  }, [searchQuery, filterConveyors, getModularConveyors]);

  useEffect(() => {
    fetchConveyors();
  }, [fetchConveyors]);

  // Reset page to 1 only when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Update filtered conveyors when allConveyors or searchQuery changes (without resetting page)
  useEffect(() => {
    if (allConveyors.length > 0) {
      const filtered = filterConveyors(allConveyors, searchQuery);
      setFilteredConveyors(filtered);
    }
  }, [allConveyors, filterConveyors, searchQuery]);

  const handleCreateConveyor = async (conveyorData) => {
    setLoading(true);
    const result = await createModularConveyor(conveyorData);
    if (result.success) {
      toast.success('Conveyor created successfully');
      setCreateModalOpen(false);
      fetchConveyors();
    } else {
      toast.error(result.error || 'Failed to create conveyor');
      setLoading(false);
    }
  };

  const handleUpdateConveyor = async (conveyorData) => { 
    if (!selectedConveyor) return;
    setLoading(true);
    const result = await updateModularConveyor(selectedConveyor._id, conveyorData);
    if (result.success) {
      setSelectedConveyor(result.conveyor);
      // Refetch conveyors to update the list (preserve current page)
      await fetchConveyors(true);
      setLoading(false);
      toast.success('Conveyor updated successfully');
      setUpdateModalOpen(false);
    } else {
      toast.error(result.error || 'Failed to update conveyor');
      setLoading(false);
    }
  };

  const handleDeleteConveyor = async () => {
    if (!selectedConveyor) return;
    setLoading(true);
    const result = await deleteModularConveyor(selectedConveyor._id);
    if (result.success) {
      toast.success('Conveyor deleted successfully');
      setDeleteOpen(false);
      fetchConveyors();
    } else {
      toast.error(result.error || 'Failed to delete conveyor');
      setLoading(false);
    }
  };

  const openDeleteModal = (conveyor) => {
    setSelectedConveyor(conveyor);
    setDeleteOpen(true);
  };

  const openUpdateModal = (conveyor) => {
    setSelectedConveyor(conveyor);
    setUpdateModalOpen(true);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredConveyors.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentConveyors = filteredConveyors.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return loading ? (
    <LoadingModal loading={loading} />
  ) : (
    <div className="p-6">
      <div className="relative mb-6 mt-3 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          Modular Conveyors
        </h2>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Add New Conveyor
        </button>
      </div>
      
      <ConveyorTable
        conveyors={currentConveyors}
        onUpdateConveyor={openUpdateModal}
        onDeleteConveyor={openDeleteModal}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />
      
      <ConveyorPagination
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        totalItems={filteredConveyors.length}
        itemsPerPage={itemsPerPage}
      />
      
      <ConfirmModal
        onConfirm={handleDeleteConveyor}
        title="Delete Conveyor"
        description="Are you sure you want to delete this conveyor?"
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      />
      
      <CreateConveyorModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleCreateConveyor}
      />
      
      <UpdateConveyorModal
        isOpen={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        onSave={handleUpdateConveyor}
        initialData={selectedConveyor}
      />
    </div>
  );
};

export default ModularConveyors;
